import React, { useState, useEffect } from 'react';
import net from 'net';
import NestedField from './NestedField';
import SiteArray from './SiteArray';
import AuthSection from './AuthSection';

const ConfigForm = ({ config, onSave }) => {
  // Parse the raw JSON string into a JavaScript object
  // const parsedConfig = JSON.parse(config.config_json); // Parse the nested config_json object
  const parsedConfig = config.config_json;
  const [formData, setFormData] = useState(parsedConfig);
  const [serviceDisabled, setServiceDisabled] = useState(config.service_disabled); // Track service_disabled state
  const [status, setStatus] = useState('Unknown'); // State for status indicator
  const [keys, setKeys] = useState([]); // State to store fetched keys
  const [isDownloading, setIsDownloading] = useState(false); // Track download state
  const [downloadStatus, setDownloadStatus] = useState(''); // Track status updates
  const [newKeyAlias, setNewKeyAlias] = useState(''); // State for new key alias
  const [addingKey, setAddingKey] = useState(false); // Track adding key state

  const checkStatus = async () => {
    if (formData.Address && formData.Port) {
      setStatus('Pinging...'); // Set status to Pinging while waiting for response
      try {
        const response = await fetch(
          `https://thor.smokyridge.solutions:5328/ping?address=${formData.Address}&port=${formData.Port}`
        );
        const data = await response.json();
        setStatus(data.status === 'online' ? 'Online' : 'Offline');
      } catch (error) {
        setStatus('Offline');
      }
    } else {
      setStatus('Unknown');
    }
  }; // Moved outside useEffect

  useEffect(() => {
    checkStatus(); // Initial check

    const intervalId = setInterval(checkStatus, 30000); // Check every 30 seconds

    return () => clearInterval(intervalId); // Cleanup interval on component unmount
  }, [formData.Address, formData.Port]); // Re-run when Address or Port changes

  useEffect(() => {
    const parsedConfig = config.config_json; // Parse the new config
    setFormData(parsedConfig); // Update the formData state with the new config
    setServiceDisabled(config.service_disabled); // Update the serviceDisabled state
  }, [config.config_json]); // Re-run this effect whenever the config prop changes

  useEffect(() => {
    if (formData.Name) {
      console.log('Fetching keys for Name:', formData.Name); // Log the Name being used for the API call
      fetch(`/api/keys/${formData.Name}`)
        .then((response) => {
          console.log('API response status:', response.status); // Log the response status
          return response.json();
        })
        .then((data) => {
          console.log('API response data:', data); // Log the data received from the API
          if (data.keys) {
            console.log('Keys found:', data.keys); // Log the keys if they exist
            setKeys(data.keys);
          } else {
            console.log('No keys found in the response.'); // Log if no keys are found
            setKeys([]);
          }
        })
        .catch((error) => {
          console.error('Error fetching keys:', error); // Log any errors during the fetch
          setKeys([]);
        });
    } else {
      console.log('No Name provided, skipping API call for keys.'); // Log if Name is not provided
    }
  }, [formData.Name]);

  const handleChange = (fieldName, value) => {
    setFormData((prev) => {
        const newData = { ...prev }; // Shallow copy of the previous state

        if (fieldName === 'Sites') {
            newData.Sites = value; // Update the Sites array with the new value
        } else if (fieldName === 'VocoderMode') {
            newData.VocoderMode = value; // Update the VocoderMode field
        } else if (fieldName === 'PreEncodeGain') {
            newData.PreEncodeGain = value; // Update the preEncodeGain field
        } else {
            const fieldPath = fieldName.split('.');
            let current = newData;

            fieldPath.slice(0, -1).forEach((key) => {
                if (!current[key]) current[key] = {}; // Ensure intermediate objects exist
                current = current[key];
            });

            current[fieldPath[fieldPath.length - 1]] = value; // Update the final field
        }

        return newData; // Return the updated state
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleDownloadScript = async () => {
    if (isDownloading) return; // Prevent multiple requests

    setIsDownloading(true);
    setDownloadStatus('Starting download process...');

    try {
      // 1. Start the job
      const startRes = await fetch(`/api/backend/start-download-script?address=${formData.Address}&port=${formData.Port}&name=${formData.Name}`);
      if (!startRes.ok) throw new Error('Failed to start download process');
      const { jobId } = await startRes.json();
      if (!jobId) throw new Error('No jobId returned from server');

      setDownloadStatus('Preparing file...');

      // 2. Poll for status
      let status = 'processing';
      let pollMessage = '';
      while (status === 'processing') {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        const pollRes = await fetch(`/api/backend/download-job-status/${jobId}`);
        if (!pollRes.ok) throw new Error('Failed to poll job status');
        const pollData = await pollRes.json();
        status = pollData.status;
        pollMessage = pollData.message;
        setDownloadStatus(pollMessage || 'Processing...');
      }

      if (status === 'complete') {
        setDownloadStatus('Download ready. Fetching file...');
        // 3. Download the file
        const fileRes = await fetch(`/api/backend/download-job-file/${jobId}`);
        if (!fileRes.ok) throw new Error('Failed to download file');
        const disposition = fileRes.headers.get('Content-Disposition');
        let filename = 'FiveM_Script.zip';
        if (disposition && disposition.indexOf('filename=') !== -1) {
          const match = disposition.match(/filename="?([^";]+)"?/);
          if (match && match[1]) filename = match[1];
        }
        const blob = await fileRes.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        setDownloadStatus('Download completed successfully.');
      } else if (status === 'error') {
        throw new Error(pollMessage || 'Download process failed');
      } else {
        throw new Error('Download process failed');
      }
    } catch (error) {
      console.error('Error during download process:', error);
      setDownloadStatus('Error: ' + error.message);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="config-form-container">
      {serviceDisabled && (
        <div className="overlay">
          <div className="overlay-message">This service has expired</div>
        </div>
      )}
      <form onSubmit={handleSubmit} className={`config-form ${serviceDisabled ? 'disabled' : ''}`}>
        <div className="api-buttons">
          <button
            type="button"
            className="api-button"
            onClick={async () => {
              if (status === 'Online') {
                alert('Server is already online.');
                return;
              }
              try {
                const response = await fetch(`/api/backend/start?name=${formData.Name}`, {
                  method: 'POST',
                });
                if (!response.ok) {
                  let errorMsg = 'Failed to start server. Please contact support.';
                  try {
                    const data = await response.json();
                    if (data && data.error) errorMsg += `\n${data.error}`;
                  } catch {}
                  alert(errorMsg);
                  return;
                }
                await response.json();
                checkStatus(); // Trigger status check
              } catch (err) {
                alert('An error occurred while starting the server. Please contact support.');
              }
            }}
            disabled={serviceDisabled}
          > Start Server </button>
          <span> </span>
          <button
            type="button"
            className="api-button"
            onClick={async () => {
              if (status === 'Offline') {
                alert('Server is already offline.');
                return;
              }
              try {
                const response = await fetch(`/api/backend/stop?name=${formData.Name}`, {
                  method: 'POST',
                });
                if (!response.ok) {
                  let errorMsg = 'Failed to stop server. Please contact support.';
                  try {
                    const data = await response.json();
                    if (data && data.error) errorMsg += `\n${data.error}`;
                  } catch {}
                  alert(errorMsg);
                  return;
                }
                await response.json();
                checkStatus(); // Trigger status check
              } catch (err) {
                alert('An error occurred while stopping the server. Please contact support.');
              }
            }}
            disabled={serviceDisabled}
          > Stop Server </button>
          <span> </span>
          <button
            type="button"
            className="api-button"
            onClick={() => {
              if (!serviceDisabled && window.confirm('Are you sure you want to update the server?\nThis will restart the server.\nNote: You must save your config changes before updating.')) {
                fetch(`/api/backend/update?name=${formData.Name}`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify(formData),
                })
                  .then((response) => response.json())
                  .then((data) => {
                    checkStatus(); // Trigger status check
                  });
              }
            }}
            disabled={serviceDisabled}
          > Update Server </button>
          <span> </span>
          <button
            type="button"
            className="api-button"
            onClick={handleDownloadScript}
            disabled={isDownloading || serviceDisabled}
          >
            {isDownloading ? 'Downloading...' : 'Download FiveM Script'}
          </button>
          <span> </span>
          <button
            type="button"
            className="api-button"
            onClick={async () => {
              if (serviceDisabled) return;
              setDownloadStatus('Preparing codeplug file...');
              try {
                const res = await fetch(`/api/backend/start-download-yml?address=${formData.Address}&port=${formData.Port}&name=${formData.Name}`);
                if (!res.ok) throw new Error('Failed to download codeplug file');
                // Get filename from Content-Disposition header
                const disposition = res.headers.get('Content-Disposition');
                let filename = 'Dispatch.yml';
                if (disposition && disposition.indexOf('filename=') !== -1) {
                  const match = disposition.match(/filename="?([^";]+)"?/);
                  if (match && match[1]) filename = match[1];
                }
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                setDownloadStatus('Codeplug file downloaded successfully.');
              } catch (error) {
                setDownloadStatus('Error: ' + error.message);
              }
            }}
            disabled={serviceDisabled}
          >
            Download Console Codeplug
          </button>
        </div>
        <div className="download-section">
          {downloadStatus && <p className="download-status">{downloadStatus}</p>}
        </div>
        <div className="top-section">
          <div className="form-field">
            <label>Name</label>
            <p>{formData.Name || 'N/A'}</p>
          </div>

          {/* Username display
          <div className="form-field">
            <label>Username</label>
            <p>{config.username || 'N/A'}</p>
          </div> */}

          <div className="form-field">
            <label>Address</label>
            <p>{formData.Address || 'N/A'}</p>
          </div>

          <div className="form-field">
            <label>Port</label>
            <p>{formData.Port || 'N/A'}</p>
          </div>

          <div className="form-field">
            <label>Status</label>
            <p style={{ fontWeight: 'bold', color: status === 'Online' ? 'green' : status === 'Offline' ? 'red' : 'inherit' }}>
              {status}
            </p>
          </div>
        </div>

        <div className="form-field">
          <label>Vocoder Mode</label>
          <select
            value={String(formData.VocoderMode) || 'IMBE'}
            onChange={(e) => handleChange('VocoderMode', e.target.value)}
            disabled={serviceDisabled}
          >
            <option value="IMBE">IMBE</option>
            <option value="DMRAMBE">DMRAMBE</option>
            <option value="DISABLED">Disabled</option>
          </select>
        </div>

        {/* New field for preEncodeGain */}
        <div className="form-field">
          <label htmlFor="PreEncodeGain">Pre-Encode Gain</label>
          <input
            id="PreEncodeGain"
            type="number"
            step="0.1"
            min="0"
            max="3"
            value={formData.PreEncodeGain !== undefined ? formData.PreEncodeGain : 1.0}
            onChange={e => handleChange('PreEncodeGain', parseFloat(e.target.value))}
            disabled={serviceDisabled}
            style={{ width: '100px' }}
          />
        </div>

        {/* New section to display keys */}
        <div className="form-field">
          <label>Auth Keys</label>
          {/* Add Key Form */}
          <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="text"
              placeholder="Key Alias"
              value={newKeyAlias || ''}
              onChange={e => {
                const value = e.target.value;
                // Allow spaces, but not only spaces, 3-32 chars
                if (/^[\w\- ]{0,32}$/.test(value)) {
                  setNewKeyAlias(value);
                }
              }}
              disabled={serviceDisabled}
              style={{ width: '200px' }}
              maxLength={32}
            />
            {/* Validation message */}
            {newKeyAlias && (newKeyAlias.trim().length < 3 || !/^[\w\- ]+$/.test(newKeyAlias) || /^\s+$/.test(newKeyAlias)) && (
              <span style={{ color: 'var(--error-color)', fontSize: '0.9em' }}>
                Key Alias must be 3-32 characters, only letters, numbers, dashes, underscores, and spaces (not only spaces).
              </span>
            )}
            <button
              type="button"
              className="api-button"
              onClick={async () => {
                if (!newKeyAlias || serviceDisabled) return;
                setAddingKey(true);
                try {
                  const res = await fetch(`/api/keys/${formData.Name}/add`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ key_alias: newKeyAlias })
                  });
                  if (!res.ok) throw new Error('Failed to add key');
                  const data = await res.json();
                  setKeys(prev => [...prev, data.key]);
                  setNewKeyAlias('');
                } catch (err) {
                  alert('Error adding key: ' + err.message);
                } finally {
                  setAddingKey(false);
                }
              }}
              disabled={serviceDisabled || !newKeyAlias || addingKey || newKeyAlias.trim().length < 3 || !/^[\w\- ]+$/.test(newKeyAlias) || /^\s+$/.test(newKeyAlias)}
            >
              {addingKey ? 'Adding...' : 'Add Key'}
            </button>
          </div>
          {keys.length > 0 ? (
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Key Alias</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Auth Key</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Created At</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((key, index) => (
                  <tr key={key.id || index}>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{key.key_alias}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', wordBreak: 'break-all' }}>{key.auth_key}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{new Date(key.created_at).toLocaleString()}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                      <button
                        type="button"
                        className="api-button"
                        onClick={async () => {
                          if (!window.confirm('Delete this key?')) return;
                          try {
                            const res = await fetch(`/api/keys/${formData.Name}/${key.id}`, { method: 'DELETE' });
                            if (!res.ok) throw new Error('Failed to delete key');
                            setKeys(prev => prev.filter(k => k.id !== key.id));
                          } catch (err) {
                            alert('Error deleting key: ' + err.message);
                          }
                        }}
                        disabled={serviceDisabled}
                      >Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No keys available for this configuration.</p>
          )}
        </div>

        <div className="site-array-container">
          <div className="overlay">
            <div className="overlay-message">Feature coming soon. If you need a site changed/added, please contact support on Discord.</div>
          </div>
          
          <SiteArray 
            sites={Array.isArray(formData.Sites) ? formData.Sites : []} 
            onChange={(updatedSites) => handleChange('Sites', updatedSites)}
            editable={!serviceDisabled} // Pass a prop to make the SiteArray editable
          />
        </div>

        <div className="form-actions">
          <button type="submit" disabled={serviceDisabled}>Save Configuration</button>
        </div>
      </form>
    </div>
  );
};

export default ConfigForm;
